import { AlloyIcon, dataTypes } from 'cat-ecommerce-alloy';
import PropTypes from 'prop-types';
import ReplacementPartsMobileView from './ReplacementPartsMobileView';
import { useTranslation } from 'react-i18next';
import UnitPriceTableHeader from '../common/UnitPriceTableHeader';
import UnitPriceTableCell from '../common/UnitPriceTableCell';
import { formatPartNumber } from '@app/store/checkout/utils';
import TooltipDisclaimer from '@app/components/common/Availability/TooltipDisclaimer';
import { getAvailability } from '../common/commonUtils';
import {
  getAvailabilityPickupText,
  getAvailabilityTitle
} from '@app/components/common/Availability/Common/util';

const displayOnDesktopOnly = 'd-none d-lg-table-cell';
const displayOnMobileOnly = 'd-lg-none';

export const ORIGINAL = 'original';
export const REPLACEMENT = 'replacement';
export const UNFORMATTED_ZERO_DOLLAR = '$0.00';

/**
 * Checks if a part is indirectly replaced given the availability field.
 * The current flag isIndirectReplacement does not work and this workaround will be removed when it is fixed.
 */
export const isIndirectReplacement = availability =>
  !!Array.isArray(availability) &&
  availability.some(
    replacementPart =>
      replacementPart?.availabilityMessage?.toLowerCase() ===
      'indirect replacement'
  );

export const getPartSchema = (
  currency,
  t,
  isQuantityMergeEnabledFlag,
  isDeliveryEnabled,
  isPickUpEnabled
) => [
  {
    id: 'item',
    label: t('ITEM'),
    className: displayOnDesktopOnly
  },
  {
    id: 'quantity',
    label: t('QUANTITY1'),
    dataType: dataTypes.NUMBER,
    className: `${displayOnDesktopOnly} text-center`
  },
  {
    id: 'availability',
    label: (
      <span>
        <span className="u-color--DarkGray u-fs--14 text-univers--bold ps-5">
          {t('SHOPCART_AVAILABILITY')}
          {!isQuantityMergeEnabledFlag && <sup>&dagger;</sup>}
          <TooltipDisclaimer />
        </span>
      </span>
    ),
    className: `${displayOnDesktopOnly} ps-5`,
    padding: '0.4rem 0.5rem'
  },
  {
    id: 'unitPrice',
    label: <UnitPriceTableHeader currency={currency} />,
    dataType: dataTypes.NUMBER,
    className: `${displayOnDesktopOnly} text-end`
  },
  {
    id: 'mobileCell',
    className: `${displayOnMobileOnly}`,
    width: '90%',
    maxHeight: 'none'
  }
];

export const LineAttributeItem = ({ icon, title }) => {
  return (
    <div className="d-flex mt-1 align-items-center">
      <span className="me-2 mb-0 mt-0 align-self-start">
        <AlloyIcon
          height={15}
          width={15}
          icon={icon}
          className="u-color--ExtraDarkGray"
        />
      </span>
      <p className="u-fs--12 u-color--ExtraDarkGray mb-0">{title}</p>
    </div>
  );
};

LineAttributeItem.propTypes = {
  icon: PropTypes.string,
  title: PropTypes.string
};

export const Quantity = ({ requestedQuantity }) => {
  const [t] = useTranslation();
  return (
    <p className="u-color--DarkGray">
      {t('QUANTITY')} {requestedQuantity}
    </p>
  );
};
Quantity.propTypes = {
  requestedQuantity: PropTypes.number
};

export const ItemDetail = ({ item }) => {
  const [t] = useTranslation();
  const { partName, partNumber, partLevelFlags } = item;
  const isRemanPart = partLevelFlags.isReman;

  return (
    <>
      <p className="u-color--DarkGray mb-2">{`${partNumber}: ${partName}`}</p>
      {isRemanPart && (
        <p className="d-flex flex-column u-fs--16 u-color--ExtraDarkGray mt-1 mb-0">
          <LineAttributeItem
            icon="icon-remanufactured"
            title={t('REMANUFACTURED_PART')}
          />
        </p>
      )}
    </>
  );
};
ItemDetail.propTypes = {
  item: PropTypes.any
};

export const getCustomRenderers = (
  currency,
  replacementParts,
  isStoreAvailabilityEnabled,
  availabilityToggle,
  hoseAvailabilityLabel
) => {
  return {
    item: item => <ItemDetail item={item} />,
    availability: item => (
      <div className="justify-content-start ps-4 ms-3">
        {!isIndirectReplacement(item?.availability)
          ? getAvailability(
              item,
              isStoreAvailabilityEnabled,
              hoseAvailabilityLabel,
              availabilityToggle
            )
          : null}
      </div>
    ),
    unitPrice: item =>
      !isIndirectReplacement(item?.availability) ? (
        <UnitPriceTableCell
          unitPrice={item?.unitPrice}
          coreCharges={item.price?.formatted?.coreCharges}
          showCoreCharges={!!item.price?.unformatted?.coreCharges}
          currency={currency}
        />
      ) : null,
    mobileCell: item =>
      item?.typeElement === ORIGINAL ? (
        <ReplacementPartsMobileView
          availabilityToggle={availabilityToggle}
          currency={currency}
          item={item}
          replacementParts={replacementParts}
          hoseAvailabilityLabel={hoseAvailabilityLabel}
        />
      ) : null
  };
};

/**
 * Returns a new items object with the replacement parts of each of them, to update the cart
 * @param {Object} items
 * @returns {Object}
 */
export const getReplacementItemsToSend = items =>
  Object.keys(items).reduce((currentItemsToSend, key) => {
    //If the item has replacement parts spread them, else just send that part
    return {
      ...currentItemsToSend,
      ...(items[key].replacementParts?.length
        ? items[key].replacementParts.reduce(
            (currentReplacementParts, replacementPart) => ({
              ...currentReplacementParts,
              [replacementPart.referenceId]: {
                ...replacementPart,
                quantity: replacementPart.quantity?.toString(),
                requestedQuantity: replacementPart.quantity?.toString(),
                //The following data is to match the corresponding values for buildDealerPriceAndAvailabilityBody
                selectedSOS: items[key].selectedSOS,
                sosList: {
                  [items[key].selectedSOS]: {
                    sosPartNumber: replacementPart.partNumber,
                    sosCode: replacementPart.sosCode
                  }
                },
                originalReplacedPartNum:
                  items[key].originalReplacedPartNum ||
                  formatPartNumber(items[key].partNumber),
                originalReplacedPartId: items[key].referenceId,
                assetId: items[key].assetId,
                customerPartNumber: items[key].customerPartNumber,
                lineItemNote: items[key].lineItemNote,
                customerItemNumber: items[key].customerItemNumber
              }
            }),
            {}
          )
        : { [key]: items[key] })
    };
  }, {});
